<template>
  <PForm
    ref="pForm"
    heading="Process Payment Request"
    :primary-cta="submitButton"
    :secondary-cta="secondaryCta"
    @submit.prevent="confirmDialog"
    @close="closeForm"
  >
    <div class="row q-col-gutter-x-lg">
      <div class="col-12">
        <p class="text-weight-regular text-neutral">
          Type of Payment:
          <span v-if="unref(paymentRequest)?.is_advance"> Vendor Advance</span>
          <span v-else>Vendor Payment </span>
        </p>
      </div>
    </div>
    <div class="row q-col-gutter-x-lg q-mt-sm">
      <div class="col-8">
        <PSelectInput
          v-model="formData.transfer_from_bank"
          :options="unref(bankFilter.entities)"
          :options-loading="unref(bankFilter.loading)"
          option-label="bank_name"
          input-debounce="300"
          label="Select the bank"
          placeholder="Select a bank"
          help="Poshn’s bank account from which transfer was made"
          required
          virtual-scroll-slice-size="25"
          virtual-scroll-item-size="53"
          @filter="bankFilter.onFilter"
          @virtual-scroll="bankFilter.onScroll"
          @filter-abort="bankFilter.onFilterAbort"
        >
          <template #option="{ itemProps, opt }">
            <q-item v-bind="itemProps">
              <q-item-section>
                <q-item-label>
                  <span class="text-neutral">
                    {{ opt.bank_name }} | A/C {{ " " }} {{ opt.account_number }}
                  </span>
                </q-item-label>
                <q-item-label caption lines="1">
                  <span class="text-neutral-70">
                    IFSC: {{ " " }} {{ opt.ifsc_code }}
                  </span>
                </q-item-label>
              </q-item-section>
            </q-item>
          </template>
        </PSelectInput>
      </div>
      <div class="col-4">
        <PSelectInput
          v-model="formData.mode"
          :options="transferModes"
          option-label="label"
          option-value="value"
          label="Transfer mode"
          placeholder="Select an option"
          help="IMPS/NEFT/RTGS"
          required
        />
      </div>
    </div>
    <div class="row q-col-gutter-x-lg q-mt-xs">
      <div class="col-6">
        <PVDateInput
          v-model="formData.date"
          label="Date"
          placeholder="DD/MM/YYYY"
          required
          :min-date="currentDate"
          :mask="formatDate(formData.date, 'DD/MM/YYYY')"
          type="date"
        />
      </div>
      <div class="col-6">
        <PVDateInput
          v-model="formData.time"
          label="Time"
          placeholder="h:mm a"
          required
          type="time"
          :date-time-rules="timeRules"
        />
      </div>
    </div>
    <div class="row q-col-gutter-x-lg q-mt-xs">
      <div class="col-6">
        <PInput
          v-model.number="unref(paymentRequest).net_payable"
          label="Net Payable"
          min="0"
          required
          placeholder="Enter value"
          step="any"
          type="number"
          disable
          class="prepend-bg"
        >
          <template #prepend>
            <span class="text-sm">₹</span>
          </template>
        </PInput>
      </div>
      <div class="col-6">
        <PInput
          v-model.number="unref(paymentRequest).used_credits"
          label="Applied Credits"
          min="0"
          required
          placeholder="Enter value"
          step="any"
          type="number"
          disable
          class="prepend-bg"
        >
          <template #prepend>
            <span class="text-sm">₹</span>
          </template>
        </PInput>
      </div>
    </div>
    <div class="row q-col-gutter-x-md q-mt-xs">
      <div class="col-12">
        <PInput
          v-model.trim="formData.reference_number"
          label="Unique Reference number"
          placeholder="Enter urn no"
          required
        />
      </div>
    </div>
    <div class="row q-col-gutter-x-md q-mt-xs">
      <div class="col-12">
        <PInput
          v-model.trim="formData.payment_description"
          label="Description"
          placeholder="Enter transaction details"
          required
          type="textarea"
        />
      </div>
    </div>
  </PForm>
</template>

<script setup>
import { usePaymentRequestStore } from "src/stores/payment-request";
import paymentRequestProcessForm from "../models/paymentRequestProcessForm";
import PConfirmDialog from "src/components/PConfirmDialog.vue";
import { useQuasar, date } from "quasar";

const { formatDate } = date;

const secondaryCta = ref({});

const pForm = ref(null);

const currentDate = ref(new Date());

const timeRules = ref({
  minutes: { interval: 15 },
});

const transferModes = ref([
  {
    label: "IMPS",
    value: "IMPS",
  },
  {
    label: "NEFT",
    value: "NEFT",
  },
  {
    label: "RTGS",
    value: "RTGS",
  },
]);

const { processPaymentDrawerState, paymentRequest, processPaymentRequest } =
  storeToRefs(usePaymentRequestStore());

const { toggleProcessPaymentForm, processPR } = usePaymentRequestStore();

const bankFilter = useFilter({
  entity: "bank",
  options: { params: { applies_to: "poshn" } },
});

const $q = useQuasar();

const confirmDialog = () => {
  $q.dialog({
    component: PConfirmDialog,
    componentProps: {
      title: "Process Payment",
      labelClass: "text-center q-ma-xl rounded-borders",
      label: `Are you sure you want to move it to payments processed?`,
      isFooterVisible: true,
      submitLabel: "OK",
      submittable: true,
      cancelLabel: "Cancel",
      cancellable: true,
      closable: true,
      onConfirm: async () => {
        await processPR(unref(formData));

        closeForm();
      },
    },
  });
};

const { formData, isLoading, closeForm } = useForm({
  formModel: paymentRequestProcessForm(),
  entity: processPaymentRequest,
  formRef: pForm,
  isForm: processPaymentDrawerState,
  toggleForm: toggleProcessPaymentForm,
});

const submitButton = computed(() => {
  return {
    loading: unref(isLoading),
    label: "Submit",
  };
});
</script>
